import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { DialogA11y } from "../controllers/dialog-a11y";
import {
  activeModal,
  approveModal,
  cancelModal,
  modalContext,
} from "../store/modal-store";

/**
 * Base modal component with accessibility features.
 * All modals should extend this class for consistent behavior.
 *
 * Features:
 * - Light DOM (Tailwind styles work directly)
 * - Click outside to close (optional)
 * - Focus trap, Escape to close, scroll lock and focus restoration, all
 *   delegated to the shared `DialogA11y` controller
 *
 * Usage:
 * 1. Extend this class
 * 2. Set the `name` property to match your modal registration
 * 3. Override `render()` to provide custom content, keeping a `.modal-dialog`
 *    element (or overriding `dialogSelector`) so the focus trap finds it
 * 4. Call `handleApprove()` or `handleCancel()` from your buttons
 */
@customElement("modal-base")
export class ModalBase extends LitElement {
  /** Modal name (must match registration in modal-store) */
  @property() name = "";

  /** Allow closing by clicking outside the modal */
  @property({ type: Boolean }) allowOutsideClick = true;

  /** Show close button in header */
  @property({ type: Boolean }) showCloseButton = true;

  /**
   * Accessible name for the dialog, used by the fallback `render()` below.
   * Subclasses that render their own markup normally supply a visible heading
   * and point `aria-labelledby` at it instead.
   */
  @property() label = "Dialog";

  @state() protected _isOpen = false;

  /**
   * Selector for the element carrying `role="dialog"` — the focus trap
   * container. Subclasses that render a different structure can override it.
   */
  protected dialogSelector = ".modal-dialog";

  /** Focus trap, Escape handling, scroll lock and focus restoration. */
  protected _a11y = new DialogA11y(this, {
    getDialog: () => this.querySelector<HTMLElement>(this.dialogSelector),
    onDismiss: () => this.handleCancel(),
    allowOutsideClick: () => this.allowOutsideClick,
    getReturnFocus: () => modalContext.value?.triggerButton ?? null,
  });

  constructor() {
    super();

    // Subscribe to activeModal signal
    effect(() => {
      const shouldBeOpen = activeModal.value === this.name;

      if (shouldBeOpen !== this._isOpen) {
        this._isOpen = shouldBeOpen;
        this._a11y.setOpen(this._isOpen);
      }
    });
  }

  // Light DOM — no shadow root (allows Tailwind to work)
  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  // ─── Event Handlers ───────────────────────────────────────────────────────

  private _handleOverlayClick(e: Event) {
    if (!this.allowOutsideClick) return;

    // Only close if clicking the overlay itself, not child elements
    if (e.target === e.currentTarget) {
      this.handleCancel();
    }
  }

  // ─── Public Actions ───────────────────────────────────────────────────────

  /**
   * Approve/confirm the modal.
   * Override this method to add custom validation before approval.
   */
  protected handleApprove() {
    approveModal();
  }

  /**
   * Cancel/close the modal.
   * Override this method to add custom cleanup on cancel.
   */
  protected handleCancel() {
    cancelModal();
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  render() {
    if (!this._isOpen) return html``;

    return html`
      <div
        class="modal-overlay fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        @click=${this._handleOverlayClick}
      >
        <div
          class="modal-dialog bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto relative"
          @click=${(e: Event) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label=${this.label}
        >
          ${this.showCloseButton
            ? html`
                <button
                  class="modal-close absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl leading-none w-8 h-8 flex items-center justify-center"
                  @click=${this.handleCancel}
                  aria-label="Close modal"
                  type="button"
                >
                  <span aria-hidden="true">×</span>
                </button>
              `
            : ""}

          <div class="modal-content p-6"></div>
        </div>
      </div>
    `;
  }
}
